import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type ReactNode, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { SelectableCard, SelectableCardIndicator } from '@/components/data-display/selectable-card'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { DrawerFormLayout, DrawerFormRow } from '@/components/forms/drawer-form-layout'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { PasswordInput } from '@/components/forms/password-input'
import { Select, type SelectOption } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { Textarea } from '@/components/forms/textarea'
import { Drawer } from '@/components/overlays/drawer'
import {
  DrawerStepProgress,
  DrawerStepTitle,
  DrawerTabs,
  DrawerWizardFooter,
} from '@/components/overlays/drawer-wizard'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Button } from '@/components/primitives/button'
import { ApiError } from '@/lib/api/http-client'

import {
  createRegisteredModel,
  getModelProbeDetail,
  getRegisteredModelDetail,
  listFallbackCandidates,
  updateModelProbeConfig,
  updateRegisteredModel,
  updateModelFallbackConfig,
  type ModelConfigField,
  type ModelConfigValue,
  type ModelFallbackTriggerMode,
  type ModelProviderMeta,
  type ModelProviderOption,
  type ModelProbeHistoryItem,
  type ModelProbeStatus,
  type ModelProbeSummary,
  type RegisteredModel,
  type RegisteredModelFormValues,
} from './api'
import { ProviderLogo } from './provider-logo'

type DrawerMode = 'create' | 'edit'
type CreateWizardStep = 'provider' | 'config' | 'pricing' | 'probe' | 'fallback'
type EditDrawerTab = 'config' | 'pricing' | 'probe' | 'fallback'

interface AdminModelProviderFormDrawerProps {
  meta?: ModelProviderMeta
  mode: DrawerMode
  modelId?: string | null
  onOpenChange: (open: boolean) => void
  open: boolean
}

type FormErrors = Partial<Record<keyof RegisteredModelFormValues, string>> &
  Record<string, string | undefined>

const createWizardSteps = ['provider', 'config', 'pricing', 'probe', 'fallback'] as const
const editDrawerTabs = ['config', 'pricing', 'probe', 'fallback'] as const

const emptyFormValues: RegisteredModelFormValues = {
  baseUrl: '',
  config: {},
  fallbackEnabled: false,
  fallbackTargetModelId: '',
  fallbackTriggerMode: 'probe_or_runtime',
  inputPricePerMillion: '',
  interfaceType: '',
  modelName: '',
  outputPricePerMillion: '',
  pricingCurrency: '',
  probeEnabled: false,
  probeIntervalMinutes: '60',
  probeRecoveryIntervalMinutes: '5',
  probeWarningLatencyMs: '3000',
  provider: '',
  publicModelName: '',
  status: 'active',
}

const probeStatusTone: Record<ModelProbeStatus, 'danger' | 'neutral' | 'success' | 'warning'> = {
  failed: 'danger',
  healthy: 'success',
  slow: 'warning',
  unknown: 'neutral',
}

const currencyOptions: SelectOption[] = [
  { label: '-', value: '' },
  { label: 'CNY', value: 'CNY' },
  { label: 'USD', value: 'USD' },
]

const fallbackTriggerModeValues: ModelFallbackTriggerMode[] = [
  'probe_or_runtime',
  'probe_only',
  'runtime_only',
]

function configValueToString(value: ModelConfigValue | undefined) {
  if (typeof value === 'string') {
    return value
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  return ''
}

function configValueToBoolean(value: ModelConfigValue | undefined) {
  return value === true || value === 'true' || value === 'True'
}

function isHttpUrl(value: string) {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function matchesValidationPattern(value: string, pattern: string) {
  if (!pattern.trim()) {
    return true
  }

  try {
    return new RegExp(pattern).test(value)
  } catch {
    return true
  }
}

function formatApiError(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.message
  }

  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message
  }

  return fallback
}

function formatOptionalDateTime(value?: string | null) {
  if (!value) {
    return '-'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  return date.toLocaleString()
}

function formatProbeMilliseconds(value?: number | null) {
  return typeof value === 'number' && Number.isFinite(value)
    ? `${String(Math.round(value))}ms`
    : '-'
}

function formatProbeTps(value?: number | null) {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(1) : '-'
}

function latestProbeStatus(summary?: ModelProbeSummary): ModelProbeStatus {
  return summary?.latest_result?.status ?? summary?.config?.latest_status ?? 'unknown'
}

function latestProbeLatency(summary?: ModelProbeSummary) {
  return summary?.latest_result?.latency_ms ?? summary?.config?.latest_latency_ms ?? null
}

function latestProbeTtft(summary?: ModelProbeSummary) {
  return summary?.latest_result?.ttft_ms ?? summary?.config?.latest_ttft_ms ?? null
}

function latestProbeThroughput(summary?: ModelProbeSummary) {
  return summary?.latest_result?.throughput_tps ?? summary?.config?.latest_throughput_tps ?? null
}

function probeNumberFieldIsInvalid(value: string, min: number, max: number) {
  const parsed = Number(value)

  return !Number.isInteger(parsed) || parsed < min || parsed > max
}

function initialValues(model?: RegisteredModel | null): RegisteredModelFormValues {
  if (!model) {
    return emptyFormValues
  }

  const inputPrice =
    model.input_price_per_million != null ? Number(model.input_price_per_million) : null
  const outputPrice =
    model.output_price_per_million != null ? Number(model.output_price_per_million) : null

  return {
    baseUrl: model.base_url,
    config: Object.fromEntries(
      Object.entries(model.config).map(([key, value]) => [key, value ?? '']),
    ),
    fallbackEnabled: Boolean(model.fallback_summary?.config.enabled),
    fallbackTargetModelId: model.fallback_summary?.target_model?.id ?? '',
    fallbackTriggerMode: model.fallback_summary?.config.trigger_mode ?? 'probe_or_runtime',
    inputPricePerMillion:
      inputPrice != null && Number.isFinite(inputPrice) ? inputPrice.toFixed(2) : '',
    interfaceType: model.interface_type,
    modelName: model.model_name,
    outputPricePerMillion:
      outputPrice != null && Number.isFinite(outputPrice) ? outputPrice.toFixed(2) : '',
    pricingCurrency: model.pricing_currency ?? '',
    probeEnabled: Boolean(model.probe_summary?.config?.enabled),
    probeIntervalMinutes: String(model.probe_summary?.config?.interval_minutes ?? 60),
    probeRecoveryIntervalMinutes: String(
      model.probe_summary?.config?.recovery_interval_minutes ?? 5,
    ),
    probeWarningLatencyMs: String(model.probe_summary?.config?.warning_latency_ms ?? 3000),
    provider: model.provider,
    publicModelName: model.public_model_name,
    status: model.status,
  }
}

function formatProviderLabel(model: RegisteredModel) {
  return model.provider_label ?? model.provider.toUpperCase()
}

function findDefaultInterface(provider?: ModelProviderOption) {
  return provider?.interfaces[0]
}

function optionValueAndLabel(option: Record<string, string>) {
  const firstEntry = Object.entries(option).at(0)

  if (!firstEntry) {
    return { label: '', value: '' }
  }

  const [value, label] = firstEntry

  return { label, value }
}

function nextCreateStep(step: CreateWizardStep) {
  const index = createWizardSteps.indexOf(step)

  return createWizardSteps[Math.min(index + 1, createWizardSteps.length - 1)]
}

function previousCreateStep(step: CreateWizardStep) {
  const index = createWizardSteps.indexOf(step)

  return createWizardSteps[Math.max(index - 1, 0)]
}

function createStepForErrorKey(errorKey: string): CreateWizardStep {
  if (errorKey === 'provider' || errorKey === 'interfaceType') {
    return 'provider'
  }

  if (errorKey.startsWith('fallback')) {
    return 'fallback'
  }

  if (errorKey.startsWith('probe')) {
    return 'probe'
  }

  if (errorKey === 'pricingCurrency') {
    return 'pricing'
  }

  return 'config'
}

function editTabForErrorKey(errorKey: string): EditDrawerTab {
  if (
    errorKey === 'pricingCurrency' ||
    errorKey === 'inputPricePerMillion' ||
    errorKey === 'outputPricePerMillion'
  ) {
    return 'pricing'
  }

  if (errorKey.startsWith('fallback')) {
    return 'fallback'
  }

  if (errorKey.startsWith('probe')) {
    return 'probe'
  }

  return 'config'
}

function fieldElementId(errorKey: string) {
  const fieldIds: Partial<Record<keyof RegisteredModelFormValues, string>> = {
    baseUrl: 'registered-model-baseUrl',
    fallbackEnabled: 'registered-model-fallbackEnabled',
    fallbackTargetModelId: 'registered-model-fallbackTargetModelId',
    fallbackTriggerMode: 'registered-model-fallbackTriggerMode',
    interfaceType: 'registered-model-interfaceType',
    inputPricePerMillion: 'registered-model-inputPricePerMillion',
    modelName: 'registered-model-modelName',
    outputPricePerMillion: 'registered-model-outputPricePerMillion',
    pricingCurrency: 'registered-model-pricingCurrency',
    probeEnabled: 'registered-model-probeEnabled',
    probeIntervalMinutes: 'registered-model-probeIntervalMinutes',
    probeRecoveryIntervalMinutes: 'registered-model-probeRecoveryIntervalMinutes',
    probeWarningLatencyMs: 'registered-model-probeWarningLatencyMs',
    provider: 'registered-model-provider',
    publicModelName: 'registered-model-publicModelName',
    status: 'registered-model-status',
  }

  return fieldIds[errorKey as keyof RegisteredModelFormValues] ?? `registered-model-${errorKey}`
}

export function AdminModelProviderFormDrawer({
  meta,
  mode,
  modelId,
  onOpenChange,
  open,
}: AdminModelProviderFormDrawerProps) {
  const { t } = useTranslation('adminModelProvider')

  const detailQuery = useQuery({
    enabled: open && mode === 'edit' && Boolean(modelId),
    queryFn: () => getRegisteredModelDetail(modelId ?? ''),
    queryKey: ['admin-registered-model-detail', modelId],
  })

  const drawerTitle =
    mode === 'create'
      ? t('drawer.createTitle')
      : t('drawer.editTitle', { name: detailQuery.data?.public_model_name ?? '' })

  const isLoading = mode === 'edit' && detailQuery.isLoading
  const isDetailError = mode === 'edit' && detailQuery.isError

  if (isLoading || isDetailError) {
    return (
      <Drawer onOpenChange={onOpenChange} open={open} size="lg" title={drawerTitle}>
        {isLoading ? (
          <div className="grid gap-4">
            <div className="h-24 animate-pulse rounded-lg bg-[var(--color-skeleton)]" />
            <div className="h-48 animate-pulse rounded-lg bg-[var(--color-skeleton)]" />
          </div>
        ) : null}
        {isDetailError ? (
          <ErrorState
            action={
              <Button onClick={() => void detailQuery.refetch()} size="sm" variant="secondary">
                {t('retry')}
              </Button>
            }
            description={formatApiError(detailQuery.error, t('drawer.detailLoadFailed'))}
            title={t('drawer.detailLoadFailedTitle')}
          />
        ) : null}
      </Drawer>
    )
  }

  return (
    <KeyedModelProviderFormContent
      key={`${mode}-${modelId ?? 'new'}-${open ? 'open' : 'closed'}-${
        detailQuery.data?.updated_at ?? 'empty'
      }`}
      meta={meta}
      mode={mode}
      model={mode === 'edit' ? detailQuery.data : null}
      modelId={modelId}
      onOpenChange={onOpenChange}
      open={open}
      title={drawerTitle}
    />
  )
}

function EditDrawerTabs({
  activeTab,
  onTabChange,
}: {
  activeTab: EditDrawerTab
  onTabChange: (tab: EditDrawerTab) => void
}) {
  const { t } = useTranslation('adminModelProvider')

  return (
    <DrawerTabs
      activeTab={activeTab}
      ariaLabel={t('drawer.editTabs.label')}
      onTabChange={onTabChange}
      tabs={editDrawerTabs.map((tab) => ({
        id: tab,
        label: t(`drawer.editTabs.${tab}`),
        panelId: `registered-model-edit-panel-${tab}`,
        tabId: `registered-model-edit-tab-${tab}`,
      }))}
    />
  )
}

function CreateStepTitle({ step }: { step: CreateWizardStep }) {
  const { t } = useTranslation('adminModelProvider')

  return <DrawerStepTitle title={t(`drawer.wizard.steps.${step}.title`)} />
}

function FieldHelp({ content }: { content: string }) {
  return <HelpTooltip label={content}>{content}</HelpTooltip>
}

function ProbeTimeline({
  history,
  statusLabels,
}: {
  history?: ModelProbeHistoryItem[]
  statusLabels: Record<ModelProbeStatus, string>
}) {
  const items: ModelProbeHistoryItem[] = history?.length
    ? history
    : Array.from({ length: 24 }, (_, index) => ({
        label: `${String(index).padStart(2, '0')}:00`,
        status: 'unknown',
      }))

  return (
    <div
      className="grid gap-1"
      role="list"
      style={{ gridTemplateColumns: `repeat(${String(items.length)}, minmax(0, 1fr))` }}
    >
      {items.map((item, index) => (
        <span
          aria-label={`${item.label} ${statusLabels[item.status]}`}
          className={[
            'h-5 min-w-0 rounded-[3px]',
            item.status === 'healthy'
              ? 'bg-[rgba(22,130,60,0.22)]'
              : item.status === 'slow'
                ? 'bg-[rgba(245,158,11,0.26)]'
                : item.status === 'failed'
                  ? 'bg-[rgba(217,45,32,0.22)]'
                  : 'bg-[var(--color-surface-muted)]',
          ].join(' ')}
          key={`${item.label}-${String(index)}`}
          role="listitem"
          title={`${item.label} / ${statusLabels[item.status]}`}
        />
      ))}
    </div>
  )
}

function ProbeMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-[var(--color-panel-border)] bg-white px-3 py-2">
      <div className="text-xs text-[var(--color-text-muted)]">{label}</div>
      <div className="mt-1 text-sm font-semibold text-[var(--color-text)]">{value}</div>
    </div>
  )
}

function KeyedModelProviderFormContent({
  meta,
  mode,
  model,
  modelId,
  onOpenChange,
  open,
  title,
}: {
  meta?: ModelProviderMeta
  mode: DrawerMode
  model?: RegisteredModel | null
  modelId?: string | null
  onOpenChange: (open: boolean) => void
  open: boolean
  title: string
}) {
  const { t } = useTranslation('adminModelProvider')
  const { dismissFeedback, showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [values, setValues] = useState<RegisteredModelFormValues>(() => initialValues(model))
  const [errors, setErrors] = useState<FormErrors>({})
  const [createStep, setCreateStep] = useState<CreateWizardStep>('provider')
  const [editTab, setEditTab] = useState<EditDrawerTab>('config')

  const probeDetailQuery = useQuery({
    enabled: open && mode === 'edit' && Boolean(modelId),
    placeholderData: (previous) => previous,
    queryFn: () => getModelProbeDetail(modelId ?? ''),
    queryKey: ['admin-registered-model-probe-detail', modelId],
  })

  const providerOptions: SelectOption[] = useMemo(
    () =>
      (meta?.providers ?? []).map((provider) => ({
        icon: (
          <ProviderLogo
            className="size-5"
            label={provider.label}
            provider={provider.key}
            variant="compact"
          />
        ),
        label: provider.label,
        value: provider.key,
      })),
    [meta],
  )

  const selectedProvider = useMemo(
    () => meta?.providers.find((provider) => provider.key === values.provider),
    [meta, values.provider],
  )

  const interfaceOptions: SelectOption[] = useMemo(
    () =>
      (selectedProvider?.interfaces ?? []).map((item) => ({
        label: item.label,
        value: item.key,
      })),
    [selectedProvider],
  )

  const selectedInterface = useMemo(
    () => meta?.interfaces.find((item) => item.key === values.interfaceType),
    [meta, values.interfaceType],
  )

  const primaryConfigFields = useMemo(
    () => (selectedInterface?.fields ?? []).filter((field) => field.key === 'api_key'),
    [selectedInterface?.fields],
  )

  const secondaryConfigFields = useMemo(
    () => (selectedInterface?.fields ?? []).filter((field) => field.key !== 'api_key'),
    [selectedInterface?.fields],
  )

  const translatedFallbackTriggerOptions = useMemo(
    () =>
      fallbackTriggerModeValues.map((value) => ({
        label: t(`drawer.fallback.triggerModes.${value}`),
        value,
      })),
    [t],
  )

  const fallbackCandidatesQuery = useQuery({
    enabled: open && Boolean(values.interfaceType),
    placeholderData: (previous) => previous,
    queryFn: () =>
      listFallbackCandidates({
        currentTargetModelId: values.fallbackTargetModelId,
        excludeModelId: modelId,
        interfaceType: values.interfaceType,
      }),
    queryKey: [
      'admin-registered-model-fallback-candidates',
      values.interfaceType,
      modelId,
      values.fallbackTargetModelId,
    ],
  })

  const fallbackCandidates = useMemo(
    () => fallbackCandidatesQuery.data?.items ?? [],
    [fallbackCandidatesQuery.data?.items],
  )

  const fallbackTargetOptions: SelectOption[] = useMemo(
    () => [
      { label: t('drawer.fallback.targetPlaceholder'), value: '' },
      ...fallbackCandidates.map((candidate) => ({
        icon: (
          <ProviderLogo
            className="size-5"
            label={formatProviderLabel(candidate)}
            provider={candidate.provider}
            variant="compact"
          />
        ),
        label: `${formatProviderLabel(candidate)} / ${candidate.public_model_name}`,
        value: candidate.id,
      })),
    ],
    [fallbackCandidates, t],
  )
  const fallbackTargetSelected = fallbackCandidates.some(
    (candidate) => candidate.id === values.fallbackTargetModelId,
  )
  const fallbackTargetValid =
    !values.fallbackTargetModelId || fallbackCandidatesQuery.isLoading || fallbackTargetSelected
  const fallbackTargetSelectValue = fallbackTargetValid ? values.fallbackTargetModelId : ''

  const mutation = useMutation({
    mutationFn: async () => {
      const savedModel =
        mode === 'edit'
          ? await updateRegisteredModel(modelId ?? '', values)
          : await createRegisteredModel(values)

      const hasExistingProbeConfig = Boolean(model?.probe_summary?.config)
      const shouldSyncProbe = Boolean(
        savedModel.id && (values.probeEnabled || hasExistingProbeConfig),
      )
      const hasExistingFallbackConfig =
        Boolean(model?.fallback_summary?.config.enabled) ||
        Boolean(model?.fallback_summary?.target_model)
      const shouldSyncFallback = Boolean(
        savedModel.id &&
        (values.fallbackEnabled || values.fallbackTargetModelId || hasExistingFallbackConfig),
      )
      const warningDetails: string[] = []

      if (shouldSyncProbe) {
        try {
          await updateModelProbeConfig(savedModel.id, values)
        } catch (error) {
          warningDetails.push(formatApiError(error, t('drawer.feedback.probeSaveFailed')))
        }
      }

      if (!shouldSyncFallback) {
        return { model: savedModel, warningDetail: warningDetails.join('\n') || null }
      }

      try {
        await updateModelFallbackConfig(savedModel.id, values)
      } catch (error) {
        warningDetails.push(formatApiError(error, t('drawer.feedback.fallbackSaveFailed')))
      }

      return { model: savedModel, warningDetail: warningDetails.join('\n') || null }
    },
    onError: (error) => {
      showFeedback({
        detail: formatApiError(error, t('drawer.submitFailed')),
        message: t('drawer.feedback.submitFailed'),
        scope: 'drawer',
        tone: 'error',
      })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-registered-models'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-registered-model-probe-detail'] })
      void queryClient.invalidateQueries({
        queryKey: ['admin-registered-model-fallback-candidates'],
      })
    },
    onSuccess: ({ warningDetail }) => {
      dismissFeedback('drawer')
      showFeedback({
        detail: warningDetail ?? undefined,
        message: warningDetail
          ? t('drawer.feedback.partialSaveFailed')
          : mode === 'create'
            ? t('drawer.feedback.createSuccess')
            : t('drawer.feedback.updateSuccess'),
        scope: 'global',
        tone: warningDetail ? 'warning' : 'success',
      })
      onOpenChange(false)
    },
  })

  useEffect(() => {
    if (!open) {
      dismissFeedback('drawer')
    }
  }, [dismissFeedback, open])

  function updateValue<Key extends keyof RegisteredModelFormValues>(
    key: Key,
    value: RegisteredModelFormValues[Key],
  ) {
    dismissFeedback('drawer')
    setValues((current) => ({
      ...current,
      [key]: value,
    }))
    setErrors((current) => ({
      ...current,
      [key]: undefined,
    }))
  }

  function updateConfig(key: string, value: ModelConfigValue) {
    dismissFeedback('drawer')
    setValues((current) => ({
      ...current,
      config: {
        ...current.config,
        [key]: value,
      },
    }))
    setErrors((current) => ({
      ...current,
      [key]: undefined,
    }))
  }

  function handleProviderChange(providerKey: string) {
    const nextProvider = meta?.providers.find((provider) => provider.key === providerKey)
    const nextInterface = findDefaultInterface(nextProvider)
    const nextInterfaceMeta = meta?.interfaces.find((item) => item.key === nextInterface?.key)

    setValues((current) => ({
      ...current,
      baseUrl: nextInterface?.default_base_url ?? nextInterfaceMeta?.default_base_url ?? '',
      config: nextInterfaceMeta?.default_config ?? {},
      fallbackTargetModelId: '',
      interfaceType: nextInterface?.key ?? '',
      provider: providerKey,
    }))
    setErrors((current) => ({
      ...current,
      interfaceType: undefined,
      provider: undefined,
    }))
  }

  function handleInterfaceChange(interfaceType: string) {
    const nextInterface = meta?.interfaces.find((item) => item.key === interfaceType)
    const providerInterface = selectedProvider?.interfaces.find(
      (item) => item.key === interfaceType,
    )

    setValues((current) => ({
      ...current,
      baseUrl: providerInterface?.default_base_url ?? nextInterface?.default_base_url ?? '',
      config: nextInterface?.default_config ?? {},
      fallbackTargetModelId: '',
      interfaceType,
    }))
    setErrors((current) => ({
      ...current,
      interfaceType: undefined,
    }))
  }

  function translatedFieldLabel(field: ModelConfigField) {
    const key = `drawer.configFields.${field.key}`
    const translated = t(key)

    return translated === key ? field.label : translated
  }

  function translatedFieldHelp(field: ModelConfigField) {
    const key = `drawer.configFieldHelp.${field.key}`
    const translated = t(key)

    return translated === key ? field.description : translated
  }

  function translatedProviderDescription(provider: ModelProviderOption) {
    const key = `drawer.wizard.provider.descriptions.${provider.key}`
    const translated = t(key)

    return translated === key ? t('drawer.wizard.provider.defaultDescription') : translated
  }

  function validateConfigField(field: ModelConfigField) {
    if (field.field_type === 'checkbox') {
      return undefined
    }

    const value = configValueToString(values.config[field.key]).trim()
    const label = translatedFieldLabel(field)

    if (field.required && !(mode === 'edit' && field.is_sensitive) && !value) {
      return t('drawer.validation.fieldRequired', { field: label })
    }

    if (
      value &&
      field.validation_pattern &&
      !matchesValidationPattern(value, field.validation_pattern)
    ) {
      return t('drawer.validation.fieldInvalid', { field: label })
    }

    return undefined
  }

  function orderedErrorKeys(scope: CreateWizardStep | 'all') {
    const keys: string[] = []

    if (scope === 'all' || scope === 'provider') {
      keys.push('provider', 'interfaceType')
    }

    if (scope === 'all' || scope === 'config') {
      keys.push(
        'publicModelName',
        'baseUrl',
        ...primaryConfigFields.map((field) => field.key),
        'modelName',
        ...secondaryConfigFields.map((field) => field.key),
      )
    }

    if (scope === 'all' || scope === 'pricing') {
      keys.push('pricingCurrency')
    }

    if (scope === 'all' || scope === 'probe') {
      keys.push('probeIntervalMinutes', 'probeRecoveryIntervalMinutes', 'probeWarningLatencyMs')
    }

    if (scope === 'all' || scope === 'fallback') {
      keys.push('fallbackTargetModelId')
    }

    return keys
  }

  function focusError(errorKey: string) {
    window.requestAnimationFrame(() => {
      document.getElementById(fieldElementId(errorKey))?.focus()
    })
  }

  function validateForm(scope: CreateWizardStep | 'all' = 'all') {
    const nextErrors: FormErrors = {}
    const shouldValidateProvider = scope === 'all' || scope === 'provider'
    const shouldValidateConfig = scope === 'all' || scope === 'config'
    const shouldValidatePricing = scope === 'all' || scope === 'pricing'
    const shouldValidateProbe = scope === 'all' || scope === 'probe'
    const shouldValidateFallback = scope === 'all' || scope === 'fallback'

    if (shouldValidateProvider && !values.provider) {
      nextErrors.provider = t('drawer.validation.providerRequired')
    }

    if (shouldValidateProvider && !values.interfaceType) {
      nextErrors.interfaceType = t('drawer.validation.interfaceRequired')
    }

    if (shouldValidateConfig && !values.baseUrl.trim()) {
      nextErrors.baseUrl = t('drawer.validation.baseUrlRequired')
    } else if (shouldValidateConfig && !isHttpUrl(values.baseUrl.trim())) {
      nextErrors.baseUrl = t('drawer.validation.baseUrlInvalid')
    }

    if (shouldValidateConfig && !values.modelName.trim()) {
      nextErrors.modelName = t('drawer.validation.modelNameRequired')
    }

    if (shouldValidateConfig && !values.publicModelName.trim()) {
      nextErrors.publicModelName = t('drawer.validation.publicModelNameRequired')
    }

    const pricingTouched =
      values.pricingCurrency || values.inputPricePerMillion || values.outputPricePerMillion
    const pricingComplete =
      values.pricingCurrency && values.inputPricePerMillion && values.outputPricePerMillion
    if (shouldValidatePricing && pricingTouched && !pricingComplete) {
      nextErrors.pricingCurrency = t('drawer.validation.pricingRequired')
    }

    if (shouldValidateProbe && probeNumberFieldIsInvalid(values.probeIntervalMinutes, 5, 1440)) {
      nextErrors.probeIntervalMinutes = t('drawer.validation.probeIntervalInvalid')
    }

    if (
      shouldValidateProbe &&
      probeNumberFieldIsInvalid(values.probeRecoveryIntervalMinutes, 1, 1440)
    ) {
      nextErrors.probeRecoveryIntervalMinutes = t('drawer.validation.probeRecoveryInvalid')
    }

    if (
      shouldValidateProbe &&
      probeNumberFieldIsInvalid(values.probeWarningLatencyMs, 100, 120000)
    ) {
      nextErrors.probeWarningLatencyMs = t('drawer.validation.probeLatencyInvalid')
    }

    if (
      shouldValidateFallback &&
      values.fallbackEnabled &&
      (!values.fallbackTargetModelId || !fallbackTargetValid)
    ) {
      nextErrors.fallbackTargetModelId = t('drawer.validation.fallbackTargetRequired')
    }

    if (shouldValidateConfig) {
      for (const field of selectedInterface?.fields ?? []) {
        const fieldError = validateConfigField(field)
        if (fieldError) {
          nextErrors[field.key] = fieldError
        }
      }
    }

    setErrors(nextErrors)
    const firstErrorKey =
      orderedErrorKeys(scope).find((key) => Boolean(nextErrors[key])) ?? Object.keys(nextErrors)[0]

    if (firstErrorKey) {
      if (mode === 'create' && scope === 'all') {
        setCreateStep(createStepForErrorKey(firstErrorKey))
      }
      if (mode === 'edit' && scope === 'all') {
        setEditTab(editTabForErrorKey(firstErrorKey))
      }
      focusError(firstErrorKey)
      showFeedback({
        detail: nextErrors[firstErrorKey],
        message: t('drawer.feedback.validationFailed'),
        scope: 'drawer',
        tone: 'error',
      })
    }

    return Object.keys(nextErrors).length === 0
  }

  function submitForm() {
    if (!validateForm()) {
      return
    }

    mutation.mutate()
  }

  function handleCreatePrimaryAction() {
    if (createStep !== 'fallback') {
      if (!validateForm(createStep)) {
        return
      }

      setCreateStep(nextCreateStep(createStep))
      return
    }

    submitForm()
  }

  function handleCreateSecondaryAction() {
    if (createStep === 'provider') {
      onOpenChange(false)
      return
    }

    setCreateStep(previousCreateStep(createStep))
  }

  function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault()

    if (mode === 'create') {
      handleCreatePrimaryAction()
      return
    }

    submitForm()
  }

  function renderConfigField(field: ModelConfigField) {
    const fieldId = `registered-model-${field.key}`
    const label = translatedFieldLabel(field)
    const error = errors[field.key]
    const maskedSecretValue =
      mode === 'edit' && field.is_sensitive
        ? configValueToString(model?.masked_config?.[`masked_${field.key}`])
        : ''
    const help = translatedFieldHelp(field)
    const labelExtra = help ? <FieldHelp content={help} /> : undefined

    if (field.field_type === 'checkbox') {
      return (
        <FormField
          error={error}
          htmlFor={fieldId}
          key={field.key}
          label={label}
          labelExtra={labelExtra}
        >
          <div className="flex h-[38px] items-center">
            <Switch
              checked={configValueToBoolean(values.config[field.key])}
              id={fieldId}
              onCheckedChange={(checked) => updateConfig(field.key, checked)}
            />
          </div>
        </FormField>
      )
    }

    if (field.options?.length) {
      return (
        <FormField
          error={error}
          htmlFor={fieldId}
          key={field.key}
          label={label}
          labelExtra={labelExtra}
          required={field.required}
        >
          <Select
            aria-label={label}
            id={fieldId}
            invalid={Boolean(error)}
            onValueChange={(value) => updateConfig(field.key, value)}
            options={field.options.map(optionValueAndLabel)}
            value={configValueToString(values.config[field.key])}
          />
        </FormField>
      )
    }

    if (field.field_type === 'textarea') {
      return (
        <FormField
          error={error}
          htmlFor={fieldId}
          key={field.key}
          label={label}
          labelExtra={labelExtra}
          required={field.required}
        >
          <Textarea
            id={fieldId}
            invalid={Boolean(error)}
            onChange={(event) => updateConfig(field.key, event.target.value)}
            placeholder={field.placeholder}
            value={configValueToString(values.config[field.key])}
          />
        </FormField>
      )
    }

    if (field.field_type === 'password' || field.is_sensitive) {
      return (
        <FormField
          error={error}
          htmlFor={fieldId}
          key={field.key}
          label={label}
          labelExtra={labelExtra}
          required={field.required}
        >
          <PasswordInput
            autoComplete="off"
            hideLabel={t('drawer.hideSecret')}
            id={fieldId}
            invalid={Boolean(error)}
            onChange={(event) => updateConfig(field.key, event.target.value)}
            placeholder={
              mode === 'edit' && field.is_sensitive
                ? maskedSecretValue
                  ? t('drawer.placeholders.secretWithSaved', { value: maskedSecretValue })
                  : t('drawer.placeholders.secret')
                : field.placeholder
            }
            showLabel={t('drawer.showSecret')}
            value={configValueToString(values.config[field.key])}
          />
        </FormField>
      )
    }

    return (
      <FormField
        error={error}
        htmlFor={fieldId}
        key={field.key}
        label={label}
        labelExtra={labelExtra}
        required={field.required}
      >
        <Input
          autoComplete="off"
          id={fieldId}
          invalid={Boolean(error)}
          onChange={(event) => updateConfig(field.key, event.target.value)}
          placeholder={field.placeholder}
          value={configValueToString(values.config[field.key])}
        />
      </FormField>
    )
  }

  function renderStatusField() {
    return (
      <FormField
        htmlFor="registered-model-status"
        label={t('drawer.fields.enabled')}
        labelExtra={<FieldHelp content={t('drawer.fieldHelp.enabled')} />}
      >
        <div className="flex h-[38px] items-center gap-3">
          <Switch
            aria-label={t('drawer.fields.enabled')}
            checked={values.status === 'active'}
            id="registered-model-status"
            onCheckedChange={(checked) => updateValue('status', checked ? 'active' : 'disabled')}
          />
          <span className="text-sm text-[var(--color-text-muted)]">
            {t(`status.${values.status}`)}
          </span>
        </div>
      </FormField>
    )
  }

  function renderFallbackFields() {
    const fallbackActive = model?.fallback_summary?.config.fallback_active
    const fallbackTargetName = model?.fallback_summary?.target_model?.model_name ?? '-'

    return (
      <>
        <DrawerFormRow columns={1}>
          <FormField
            htmlFor="registered-model-fallbackEnabled"
            label={t('drawer.fallback.enabled')}
            labelExtra={<FieldHelp content={t('drawer.fallback.enabledHelp')} />}
          >
            <div className="flex h-[38px] items-center gap-3">
              <Switch
                aria-label={t('drawer.fallback.enabled')}
                checked={values.fallbackEnabled}
                id="registered-model-fallbackEnabled"
                onCheckedChange={(checked) => updateValue('fallbackEnabled', checked)}
              />
              <span className="text-sm text-[var(--color-text-muted)]">
                {values.fallbackEnabled
                  ? t('drawer.fallback.enabledState')
                  : t('drawer.fallback.disabledState')}
              </span>
            </div>
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.fallbackTargetModelId}
            htmlFor="registered-model-fallbackTargetModelId"
            label={t('drawer.fallback.targetLabel')}
            labelExtra={<FieldHelp content={t('drawer.fallback.targetHelp')} />}
          >
            <Select
              aria-label={t('drawer.fallback.targetLabel')}
              disabled={!values.fallbackEnabled || fallbackCandidatesQuery.isLoading}
              id="registered-model-fallbackTargetModelId"
              invalid={Boolean(errors.fallbackTargetModelId)}
              onValueChange={(value) => updateValue('fallbackTargetModelId', value)}
              options={fallbackTargetOptions}
              value={fallbackTargetSelectValue}
            />
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            htmlFor="registered-model-fallbackTriggerMode"
            label={t('drawer.fallback.triggerLabel')}
            labelExtra={<FieldHelp content={t('drawer.fallback.triggerHelp')} />}
          >
            <Select
              aria-label={t('drawer.fallback.triggerLabel')}
              disabled={!values.fallbackEnabled}
              id="registered-model-fallbackTriggerMode"
              onValueChange={(value) =>
                updateValue('fallbackTriggerMode', value as ModelFallbackTriggerMode)
              }
              options={translatedFallbackTriggerOptions}
              value={values.fallbackTriggerMode}
            />
          </FormField>
        </DrawerFormRow>
        {fallbackActive ? (
          <InlineNotice className="rounded-md px-3 py-2 leading-5" tone="warning">
            {t('drawer.fallback.activeHint', { model: fallbackTargetName })}
          </InlineNotice>
        ) : null}
      </>
    )
  }

  function renderProbeFields() {
    const summary = mode === 'edit' ? (probeDetailQuery.data ?? model?.probe_summary) : undefined
    const status = latestProbeStatus(summary)
    const statusLabels: Record<ModelProbeStatus, string> = {
      failed: t('probe.status.failed'),
      healthy: t('probe.status.healthy'),
      slow: t('probe.status.slow'),
      unknown: t('probe.status.unknown'),
    }

    return (
      <>
        <DrawerFormRow columns={1}>
          <FormField
            htmlFor="registered-model-probeEnabled"
            label={t('drawer.probe.enabled')}
            labelExtra={<FieldHelp content={t('drawer.probe.enabledHelp')} />}
          >
            <div className="flex h-[38px] items-center gap-3">
              <Switch
                aria-label={t('drawer.probe.enabled')}
                checked={values.probeEnabled}
                id="registered-model-probeEnabled"
                onCheckedChange={(checked) => updateValue('probeEnabled', checked)}
              />
              <span className="text-sm text-[var(--color-text-muted)]">
                {values.probeEnabled
                  ? t('drawer.probe.enabledState')
                  : t('drawer.probe.disabledState')}
              </span>
            </div>
          </FormField>
        </DrawerFormRow>
        <section className="rounded-md border border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] p-3">
          <div className="text-sm font-semibold text-[var(--color-text)]">
            {t('drawer.probe.mechanismTitle')}
          </div>
          <p className="mt-1 text-sm leading-6 text-[var(--color-text-muted)]">
            {t('drawer.probe.mechanismDescription')}
          </p>
        </section>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.probeIntervalMinutes}
            htmlFor="registered-model-probeIntervalMinutes"
            label={t('drawer.probe.intervalLabel')}
            labelExtra={<FieldHelp content={t('drawer.probe.intervalHelp')} />}
          >
            <Input
              id="registered-model-probeIntervalMinutes"
              invalid={Boolean(errors.probeIntervalMinutes)}
              min={5}
              onChange={(event) => updateValue('probeIntervalMinutes', event.target.value)}
              step={1}
              type="number"
              value={values.probeIntervalMinutes}
            />
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.probeRecoveryIntervalMinutes}
            htmlFor="registered-model-probeRecoveryIntervalMinutes"
            label={t('drawer.probe.recoveryIntervalLabel')}
            labelExtra={<FieldHelp content={t('drawer.probe.recoveryIntervalHelp')} />}
          >
            <Input
              id="registered-model-probeRecoveryIntervalMinutes"
              invalid={Boolean(errors.probeRecoveryIntervalMinutes)}
              min={1}
              onChange={(event) => updateValue('probeRecoveryIntervalMinutes', event.target.value)}
              step={1}
              type="number"
              value={values.probeRecoveryIntervalMinutes}
            />
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.probeWarningLatencyMs}
            htmlFor="registered-model-probeWarningLatencyMs"
            label={t('drawer.probe.warningLatencyLabel')}
            labelExtra={<FieldHelp content={t('drawer.probe.warningLatencyHelp')} />}
          >
            <Input
              id="registered-model-probeWarningLatencyMs"
              invalid={Boolean(errors.probeWarningLatencyMs)}
              min={100}
              onChange={(event) => updateValue('probeWarningLatencyMs', event.target.value)}
              step={100}
              type="number"
              value={values.probeWarningLatencyMs}
            />
          </FormField>
        </DrawerFormRow>
        {mode === 'edit' ? (
          <section className="grid gap-3 rounded-md border border-[var(--color-panel-border)] bg-[var(--color-surface-muted)] p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-[var(--color-text)]">
                  {t('drawer.probe.recentTitle')}
                </div>
                <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                  {t('drawer.probe.nextProbeAt', {
                    time: formatOptionalDateTime(summary?.config?.next_probe_at),
                  })}
                </div>
              </div>
              <span
                className={[
                  'rounded-full px-2.5 py-1 text-xs font-semibold',
                  probeStatusTone[status] === 'success'
                    ? 'bg-[rgba(22,130,60,0.12)] text-[var(--color-success)]'
                    : probeStatusTone[status] === 'warning'
                      ? 'bg-[rgba(245,158,11,0.14)] text-[#92400e]'
                      : probeStatusTone[status] === 'danger'
                        ? 'bg-[rgba(217,45,32,0.1)] text-[var(--color-danger)]'
                        : 'bg-white text-[var(--color-text-muted)]',
                ].join(' ')}
              >
                {statusLabels[status]}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <ProbeMetric
                label={t('probe.metrics.latency')}
                value={formatProbeMilliseconds(latestProbeLatency(summary))}
              />
              <ProbeMetric
                label={t('probe.metrics.ttft')}
                value={formatProbeMilliseconds(latestProbeTtft(summary))}
              />
              <ProbeMetric
                label={t('probe.metrics.tps')}
                value={formatProbeTps(latestProbeThroughput(summary))}
              />
              <ProbeMetric
                label={t('probe.checkedAt')}
                value={formatOptionalDateTime(summary?.latest_result?.checked_at)}
              />
            </div>
            {summary?.history?.length ? (
              <ProbeTimeline history={summary.history} statusLabels={statusLabels} />
            ) : null}
          </section>
        ) : null}
      </>
    )
  }

  function renderEditModelConfigTab() {
    return (
      <DrawerFormLayout className="mt-0">
        <DrawerFormRow columns={1}>{renderStatusField()}</DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.publicModelName}
            htmlFor="registered-model-publicModelName"
            label={t('drawer.fields.publicModelName')}
            labelExtra={<FieldHelp content={t('drawer.fieldHelp.publicModelName')} />}
            required
          >
            <Input
              id="registered-model-publicModelName"
              invalid={Boolean(errors.publicModelName)}
              onChange={(event) => updateValue('publicModelName', event.target.value)}
              placeholder={t('drawer.placeholders.publicModelName')}
              value={values.publicModelName}
            />
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.provider}
            htmlFor="registered-model-provider"
            label={t('drawer.fields.provider')}
            labelExtra={<FieldHelp content={t('drawer.fieldHelp.provider')} />}
            required
          >
            <Select
              aria-label={t('drawer.fields.provider')}
              disabled={!meta}
              id="registered-model-provider"
              invalid={Boolean(errors.provider)}
              onValueChange={handleProviderChange}
              options={providerOptions}
              value={values.provider}
            />
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.interfaceType}
            htmlFor="registered-model-interfaceType"
            label={t('drawer.fields.interfaceType')}
            labelExtra={<FieldHelp content={t('drawer.fieldHelp.interfaceType')} />}
            required
          >
            <Select
              aria-label={t('drawer.fields.interfaceType')}
              disabled={!values.provider}
              id="registered-model-interfaceType"
              invalid={Boolean(errors.interfaceType)}
              onValueChange={handleInterfaceChange}
              options={interfaceOptions}
              value={values.interfaceType}
            />
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.baseUrl}
            htmlFor="registered-model-baseUrl"
            label={t('drawer.fields.baseUrl')}
            labelExtra={<FieldHelp content={t('drawer.fieldHelp.baseUrl')} />}
            required
          >
            <Input
              id="registered-model-baseUrl"
              invalid={Boolean(errors.baseUrl)}
              onChange={(event) => updateValue('baseUrl', event.target.value)}
              placeholder={t('drawer.placeholders.baseUrl')}
              value={values.baseUrl}
            />
          </FormField>
        </DrawerFormRow>
        {primaryConfigFields.map((field) => (
          <DrawerFormRow columns={1} key={field.key}>
            {renderConfigField(field)}
          </DrawerFormRow>
        ))}
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.modelName}
            htmlFor="registered-model-modelName"
            label={t('drawer.fields.modelName')}
            labelExtra={<FieldHelp content={t('drawer.fieldHelp.modelName')} />}
            required
          >
            <Input
              id="registered-model-modelName"
              invalid={Boolean(errors.modelName)}
              onChange={(event) => updateValue('modelName', event.target.value)}
              placeholder={t('drawer.placeholders.modelName')}
              value={values.modelName}
            />
          </FormField>
        </DrawerFormRow>
        {secondaryConfigFields.map((field) => (
          <DrawerFormRow columns={1} key={field.key}>
            {renderConfigField(field)}
          </DrawerFormRow>
        ))}
      </DrawerFormLayout>
    )
  }

  function renderEditPricingTab() {
    return (
      <DrawerFormLayout className="mt-0">
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.pricingCurrency}
            htmlFor="registered-model-pricingCurrency"
            label={t('drawer.fields.pricingCurrency')}
          >
            <Select
              aria-label={t('drawer.fields.pricingCurrency')}
              id="registered-model-pricingCurrency"
              invalid={Boolean(errors.pricingCurrency)}
              onValueChange={(value) => updateValue('pricingCurrency', value)}
              options={currencyOptions}
              value={values.pricingCurrency}
            />
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.pricingCurrency}
            htmlFor="registered-model-inputPricePerMillion"
            label={t('drawer.fields.inputPricePerMillion')}
          >
            <Input
              id="registered-model-inputPricePerMillion"
              min={0}
              onChange={(event) => updateValue('inputPricePerMillion', event.target.value)}
              placeholder="0.000000"
              step="0.000001"
              type="number"
              value={values.inputPricePerMillion}
            />
          </FormField>
        </DrawerFormRow>
        <DrawerFormRow columns={1}>
          <FormField
            error={errors.pricingCurrency}
            htmlFor="registered-model-outputPricePerMillion"
            label={t('drawer.fields.outputPricePerMillion')}
          >
            <Input
              id="registered-model-outputPricePerMillion"
              min={0}
              onChange={(event) => updateValue('outputPricePerMillion', event.target.value)}
              placeholder="0.000000"
              step="0.000001"
              type="number"
              value={values.outputPricePerMillion}
            />
          </FormField>
        </DrawerFormRow>
      </DrawerFormLayout>
    )
  }

  function renderEditProbeTab() {
    return <DrawerFormLayout className="mt-0">{renderProbeFields()}</DrawerFormLayout>
  }

  function renderEditFallbackTab() {
    return <DrawerFormLayout className="mt-0">{renderFallbackFields()}</DrawerFormLayout>
  }

  const createStepIndex = createWizardSteps.indexOf(createStep)
  const canAdvanceCurrentCreateStep =
    createStep !== 'provider' || (Boolean(meta) && Boolean(values.provider))
  const drawerTitleMeta =
    mode === 'create'
      ? t('drawer.wizard.headerDescription', {
          current: createStepIndex + 1,
          total: createWizardSteps.length,
        })
      : undefined
  const drawerHeaderAddon =
    mode === 'create' ? (
      <DrawerStepProgress
        ariaLabel={t('drawer.wizard.progressLabel', {
          current: createStepIndex + 1,
          step: t(`drawer.wizard.steps.${createStep}.title`),
          total: createWizardSteps.length,
        })}
        className="w-[280px]"
        currentStep={createStep}
        steps={createWizardSteps}
      />
    ) : (
      <EditDrawerTabs activeTab={editTab} onTabChange={setEditTab} />
    )
  const drawerFooter =
    mode === 'create' ? (
      <DrawerWizardFooter
        backLabel={t('drawer.wizard.previous')}
        canGoBack={createStep !== 'provider'}
        isLoading={mutation.isPending}
        nextLabel={createStep === 'fallback' ? t('drawer.wizard.create') : t('drawer.wizard.next')}
        nextDisabled={!canAdvanceCurrentCreateStep}
        onBack={handleCreateSecondaryAction}
        onNext={handleCreatePrimaryAction}
      />
    ) : (
      <>
        <Button
          disabled={mutation.isPending}
          onClick={() => onOpenChange(false)}
          variant="secondary"
        >
          {t('drawer.cancel')}
        </Button>
        <Button form="registered-model-form" isLoading={mutation.isPending} type="submit">
          {t('drawer.save')}
        </Button>
      </>
    )

  let createStepContent: ReactNode = null

  if (createStep === 'provider') {
    createStepContent = (
      <div>
        <CreateStepTitle step={createStep} />
        <div
          aria-invalid={Boolean(errors.provider) || undefined}
          aria-label={t('drawer.fields.provider')}
          className="grid gap-3"
          id="registered-model-provider"
          role="radiogroup"
          tabIndex={-1}
        >
          {meta
            ? meta.providers.map((provider) => {
                const selected = values.provider === provider.key

                return (
                  <SelectableCard
                    as="button"
                    aria-checked={selected}
                    className="min-h-[76px] cursor-pointer px-4 py-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
                    key={provider.key}
                    onClick={() => handleProviderChange(provider.key)}
                    role="radio"
                    selected={selected}
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span className="flex min-w-0 items-start gap-3">
                        <ProviderLogo
                          className="mt-0.5 size-9 rounded-lg p-1.5"
                          label={provider.label}
                          provider={provider.key}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-bold text-[var(--color-text)]">
                            {provider.label}
                          </span>
                          <span className="mt-1 block text-xs leading-5 text-[var(--color-text-muted)]">
                            {translatedProviderDescription(provider)}
                          </span>
                        </span>
                      </span>
                      <SelectableCardIndicator selected={selected} />
                    </span>
                  </SelectableCard>
                )
              })
            : Array.from({ length: 4 }).map((_, index) => (
                <div
                  className="h-[76px] animate-pulse rounded-lg bg-[var(--color-skeleton)]"
                  key={index}
                />
              ))}
        </div>
        {errors.provider ? <p className="sr-only">{errors.provider}</p> : null}
      </div>
    )
  } else if (createStep === 'config') {
    createStepContent = (
      <div>
        <CreateStepTitle step={createStep} />
        <DrawerFormLayout className="mt-0">
          <DrawerFormRow columns={1}>
            <FormField
              error={errors.publicModelName}
              htmlFor="registered-model-publicModelName"
              label={t('drawer.fields.publicModelName')}
              labelExtra={<FieldHelp content={t('drawer.fieldHelp.publicModelName')} />}
              required
            >
              <Input
                id="registered-model-publicModelName"
                invalid={Boolean(errors.publicModelName)}
                onChange={(event) => updateValue('publicModelName', event.target.value)}
                placeholder={t('drawer.placeholders.publicModelName')}
                value={values.publicModelName}
              />
            </FormField>
          </DrawerFormRow>
          <DrawerFormRow columns={1}>
            <FormField
              error={errors.interfaceType}
              htmlFor="registered-model-interfaceType"
              label={t('drawer.fields.interfaceType')}
              labelExtra={<FieldHelp content={t('drawer.fieldHelp.interfaceType')} />}
              required
            >
              <Select
                aria-label={t('drawer.fields.interfaceType')}
                disabled={!values.provider}
                id="registered-model-interfaceType"
                invalid={Boolean(errors.interfaceType)}
                onValueChange={handleInterfaceChange}
                options={interfaceOptions}
                value={values.interfaceType}
              />
            </FormField>
          </DrawerFormRow>
          <DrawerFormRow columns={1}>
            <FormField
              error={errors.baseUrl}
              htmlFor="registered-model-baseUrl"
              label={t('drawer.fields.baseUrl')}
              labelExtra={<FieldHelp content={t('drawer.fieldHelp.baseUrl')} />}
              required
            >
              <Input
                id="registered-model-baseUrl"
                invalid={Boolean(errors.baseUrl)}
                onChange={(event) => updateValue('baseUrl', event.target.value)}
                placeholder={t('drawer.placeholders.baseUrl')}
                value={values.baseUrl}
              />
            </FormField>
          </DrawerFormRow>
          {primaryConfigFields.map((field) => (
            <DrawerFormRow columns={1} key={field.key}>
              {renderConfigField(field)}
            </DrawerFormRow>
          ))}
          <DrawerFormRow columns={1}>
            <FormField
              error={errors.modelName}
              htmlFor="registered-model-modelName"
              label={t('drawer.fields.modelName')}
              labelExtra={<FieldHelp content={t('drawer.fieldHelp.modelName')} />}
              required
            >
              <Input
                id="registered-model-modelName"
                invalid={Boolean(errors.modelName)}
                onChange={(event) => updateValue('modelName', event.target.value)}
                placeholder={t('drawer.placeholders.modelName')}
                value={values.modelName}
              />
            </FormField>
          </DrawerFormRow>
          {secondaryConfigFields.map((field) => (
            <DrawerFormRow columns={1} key={field.key}>
              {renderConfigField(field)}
            </DrawerFormRow>
          ))}
        </DrawerFormLayout>
      </div>
    )
  } else if (createStep === 'probe') {
    createStepContent = (
      <div>
        <CreateStepTitle step={createStep} />
        <DrawerFormLayout className="mt-0">{renderProbeFields()}</DrawerFormLayout>
      </div>
    )
  } else if (createStep === 'pricing') {
    createStepContent = (
      <div>
        <CreateStepTitle step={createStep} />
        <DrawerFormLayout className="mt-0">
          <DrawerFormRow columns={1}>
            <FormField
              error={errors.pricingCurrency}
              htmlFor="registered-model-pricingCurrency"
              label={t('drawer.fields.pricingCurrency')}
            >
              <Select
                aria-label={t('drawer.fields.pricingCurrency')}
                id="registered-model-pricingCurrency"
                invalid={Boolean(errors.pricingCurrency)}
                onValueChange={(value) => updateValue('pricingCurrency', value)}
                options={currencyOptions}
                value={values.pricingCurrency}
              />
            </FormField>
          </DrawerFormRow>
          <DrawerFormRow columns={1}>
            <FormField
              error={errors.pricingCurrency}
              htmlFor="registered-model-inputPricePerMillion"
              label={t('drawer.fields.inputPricePerMillion')}
            >
              <Input
                id="registered-model-inputPricePerMillion"
                min={0}
                onChange={(event) => updateValue('inputPricePerMillion', event.target.value)}
                placeholder="0.000000"
                step="0.000001"
                type="number"
                value={values.inputPricePerMillion}
              />
            </FormField>
          </DrawerFormRow>
          <DrawerFormRow columns={1}>
            <FormField
              error={errors.pricingCurrency}
              htmlFor="registered-model-outputPricePerMillion"
              label={t('drawer.fields.outputPricePerMillion')}
            >
              <Input
                id="registered-model-outputPricePerMillion"
                min={0}
                onChange={(event) => updateValue('outputPricePerMillion', event.target.value)}
                placeholder="0.000000"
                step="0.000001"
                type="number"
                value={values.outputPricePerMillion}
              />
            </FormField>
          </DrawerFormRow>
        </DrawerFormLayout>
      </div>
    )
  } else {
    createStepContent = (
      <div>
        <CreateStepTitle step={createStep} />
        <DrawerFormLayout className="mt-0">{renderFallbackFields()}</DrawerFormLayout>
      </div>
    )
  }

  return (
    <Drawer
      footer={drawerFooter}
      headerAddon={drawerHeaderAddon}
      onOpenChange={onOpenChange}
      open={open}
      size="lg"
      title={title}
      titleMeta={drawerTitleMeta}
    >
      <form id="registered-model-form" onSubmit={handleSubmit}>
        {mode === 'create' ? (
          <div className="pb-6">{createStepContent}</div>
        ) : (
          <div
            aria-labelledby={`registered-model-edit-tab-${editTab}`}
            id={`registered-model-edit-panel-${editTab}`}
            role="tabpanel"
            tabIndex={0}
          >
            {editTab === 'config' ? renderEditModelConfigTab() : null}
            {editTab === 'pricing' ? renderEditPricingTab() : null}
            {editTab === 'probe' ? renderEditProbeTab() : null}
            {editTab === 'fallback' ? renderEditFallbackTab() : null}
          </div>
        )}
      </form>
    </Drawer>
  )
}
